import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { ThirdPersonCamera } from '../camera/ThirdPersonCamera';
import type { EventsConfig } from '../config/events.config';
import type { EventBus } from '../core/EventBus';
import type { GameEvents } from '../core/GameEvents';
import type { CollapsePiece } from '../level/segments/SegmentDefinition';
import type { SegmentInstance } from '../level/segments/SegmentInstance';
import type { ObstacleManager } from '../obstacles/ObstacleManager';
import type { ObstaclePresenter } from '../obstacles/ObstaclePresenter';
import { DustBurst } from '../rendering/DustBurst';
import { clamp, DEG_TO_RAD, lerp, type Vec3 } from '../utils/math';

interface PieceState {
  readonly obstacleUid: number;
  readonly piece: CollapsePiece;
  /** Final (gameplay) position = obstacle spawn point. */
  readonly final: Vec3;
  landed: boolean;
}

interface CollapseState {
  readonly segment: SegmentInstance;
  readonly pieces: PieceState[];
  triggered: boolean;
  elapsed: number;
}

/**
 * Visual side of the "Hauseinsturz" set piece. Debris obstacles exist in
 * gameplay from the start (at their final place); this presenter first shows
 * their visuals as the standing building and, when the WALL_COLLAPSE socket is
 * armed, lets them fall onto the track. Purely visual – collision is unchanged.
 */
export class CollapsePresenter {
  private readonly states = new Map<number, CollapseState>();
  private readonly scratch = new Vector3();
  private readonly dust: DustBurst;

  constructor(
    private readonly obstacles: ObstacleManager,
    private readonly obstacleView: ObstaclePresenter,
    private readonly camera: ThirdPersonCamera,
    private readonly config: EventsConfig['collapse'],
    events: EventBus<GameEvents>,
  ) {
    this.dust = new DustBurst(camera.camera.getScene(), config.dust);
    // Subscribed after ObstacleManager/ObstaclePresenter: the debris visuals already exist here.
    events.on('segment:spawned', ({ segment }) => this.prepare(segment));
    events.on('segment:recycled', ({ segment }) => this.states.delete(segment.uid));
    events.on('dynamicEvent:armed', ({ socket }) => {
      const state = this.states.get(socket.segment.uid);
      if (!state || state.triggered || socket.socket.id !== socket.segment.definition.collapseEvent?.socketId) return;
      state.triggered = true;
      // A rumble only – the camera no longer turns towards the building (user 2026-10-03).
      this.camera.shake.addTrauma(this.config.rumbleTrauma);
    });
  }

  update(dt: number): void {
    for (const state of this.states.values()) {
      if (!state.triggered) continue;
      state.elapsed += dt;
      for (const piece of state.pieces) this.animate(state, piece);
    }
  }

  private prepare(segment: SegmentInstance): void {
    const collapse = segment.definition.collapseEvent;
    if (!collapse) return;
    const obstacles = this.obstacles.forSegment(segment.uid);
    const pieces: PieceState[] = [];
    for (const piece of collapse.pieces) {
      const obstacle = obstacles.find((o) => o.spawnPoint.id === piece.spawnPointId);
      if (!obstacle) continue;
      pieces.push({ obstacleUid: obstacle.uid, piece, final: obstacle.spawnPoint.position, landed: false });
    }
    const state: CollapseState = { segment, pieces, triggered: false, elapsed: 0 };
    this.states.set(segment.uid, state);
    for (const piece of pieces) this.pose(piece, 0);
  }

  private animate(state: CollapseState, piece: PieceState): void {
    if (piece.landed) return;
    const t = clamp((state.elapsed - piece.piece.delay) / this.config.pieceFallDuration, 0, 1);
    this.pose(piece, t);
    if (t >= 1) {
      piece.landed = true;
      this.impactShake(piece);
    }
  }

  /** t = 0: standing building, t = 1: debris on the track. */
  private pose(piece: PieceState, t: number): void {
    const visual = this.obstacleView.visualOf(piece.obstacleUid);
    if (!visual) return; // cleared or despawned
    const from = piece.piece.buildingPosition;
    const to = piece.final;
    // Horizontal travel eases out, the drop accelerates like falling.
    const horizontal = 1 - (1 - t) * (1 - t);
    visual.root.position.set(lerp(from.x, to.x, horizontal), lerp(from.y, to.y, t * t), lerp(from.z, to.z, horizontal));
    const rot = piece.piece.buildingRotation;
    const tumble = Math.sin(Math.PI * t) * this.config.tumbleDegrees;
    visual.root.rotation.set(
      (lerp(rot.x, 0, t) + tumble) * DEG_TO_RAD,
      lerp(rot.y, piece.piece.finalYaw, t) * DEG_TO_RAD,
      (lerp(rot.z, 0, t) + tumble * 0.4) * DEG_TO_RAD,
    );
  }

  /** Landing: concrete dust cloud at the piece, camera shake by proximity. */
  private impactShake(piece: PieceState): void {
    const visual = this.obstacleView.visualOf(piece.obstacleUid);
    if (!visual) return;
    const at = visual.root.getAbsolutePosition();
    this.dust.burst(at);
    this.scratch.copyFrom(this.camera.camera.globalPosition);
    const distance = Vector3.Distance(at, this.scratch);
    const proximity = clamp(1 - distance / this.config.impactShakeRange, 0, 1);
    this.camera.shake.addTrauma(this.config.impactTrauma * proximity * proximity);
  }
}
